import type { StateCreator } from "zustand";
import { isValidEmail } from "@/components/news-alerts/newsAlertsHelpers";
import toast from "@/components/ui/toast";
import {
  CreateScheduledBriefing,
  DeleteScheduledBriefing,
  GenerateScheduledBriefingPdfPreview,
  GetScheduledBriefing,
  UpdateScheduledBriefing,
} from "@/data-access/news-alerts/queries/scheduled-briefing-queries";
import type { TimezoneChoices } from "@/data-access/news-alerts/queries/timezone-queries";
import { client } from "@/lib/urqlProvider";
import { previewPdfInNewTab } from "@/lib/utils/previewPdfInNewTab";
import {
  buildGenerateBriefingPdfPreviewInput,
  buildScheduledBriefingInput,
  buildScheduledBriefingUpdateInput,
  hydrateScheduledBriefingFormFromApi,
} from "./schedule-briefing.helpers";
import {
  type ScheduledBriefingInvalidRecipient,
  type ScheduledBriefingSlice,
  scheduledBriefingDetailsInitialState,
  scheduledBriefingLogoInitialState,
  scheduledBriefingRecipientsInitialState,
  scheduledBriefingScheduleInitialState,
} from "./scheduled-briefing.slice";

const toInvalidRecipientEntry = (
  value: string,
): ScheduledBriefingInvalidRecipient => ({
  id: crypto.randomUUID(),
  value,
});

const revokeScheduledBriefingLogoPreviewUrl = (previewUrl: string | null) => {
  if (previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(previewUrl);
  }
};

let scheduledBriefingPreviewController: AbortController | null = null;

const abortScheduledBriefingPreviewRequest = () => {
  if (scheduledBriefingPreviewController) {
    scheduledBriefingPreviewController.abort();
    scheduledBriefingPreviewController = null;
  }
};

export type ScheduledBriefingActions = {
  openDeleteScheduledBriefingModal: (id: string, name: string) => void;
  closeDeleteScheduledBriefingModal: () => void;
  setCreateScheduledBriefingModalOpen: (isOpen: boolean) => void;
  resetScheduledBriefingForm: () => void;
  setScheduledBriefingTitle: (title: string) => void;
  setScheduledBriefingDescription: (description: string) => void;
  setShowScheduledBriefingDescription: (showDescription: boolean) => void;
  setScheduledBriefingPrompt: (prompt: string) => void;
  setScheduledBriefingHasUserPrompt: (hasUserPrompt: boolean) => void;
  setScheduledBriefingScheduleTime: (scheduleTime: string) => void;
  setScheduledBriefingTimezone: (timezone: TimezoneChoices | "") => void;
  setScheduledBriefingRecipientInputValue: (inputValue: string) => void;
  addScheduledBriefingRecipient: (raw: string) => void;
  removeScheduledBriefingValidRecipient: (email: string) => void;
  removeScheduledBriefingInvalidRecipient: (id: string) => void;
  setScheduledBriefingInvalidRecipientValue: (
    id: string,
    value: string,
  ) => void;
  commitScheduledBriefingInvalidRecipient: (id: string) => void;
  uploadScheduledBriefingLogo: (file: File) => void;
  removeScheduledBriefingLogo: () => void;
  createScheduledBriefing: () => Promise<boolean>;
  updateScheduledBriefing: () => Promise<boolean>;
  deleteScheduledBriefing: () => Promise<boolean>;
  generateScheduledBriefingPreview: () => Promise<void>;
  fetchScheduledBriefingById: (id: string) => Promise<boolean>;
  openScheduledBriefingForEdit: (id: string) => Promise<boolean>;
};

export const createScheduledBriefingActions: StateCreator<
  ScheduledBriefingSlice & ScheduledBriefingActions,
  [["zustand/devtools", never]],
  [],
  ScheduledBriefingActions
> = (set, get) => ({
  openDeleteScheduledBriefingModal: (id: string, name: string) =>
    set(
      {
        isDeleteScheduledBriefingModalOpen: true,
        deletingScheduledBriefingId: id,
        deletingScheduledBriefingName: name,
      },
      false,
      "[User] Scheduled Briefing: Open delete modal",
    ),

  closeDeleteScheduledBriefingModal: () =>
    set(
      {
        isDeleteScheduledBriefingModalOpen: false,
        deletingScheduledBriefingId: null,
        deletingScheduledBriefingName: "",
      },
      false,
      "[User] Scheduled Briefing: Close delete modal",
    ),

  setCreateScheduledBriefingModalOpen: (isOpen: boolean) =>
    set(
      { isCreateScheduledBriefingModalOpen: isOpen },
      false,
      "[User] Scheduled Briefing: Set create modal open",
    ),

  resetScheduledBriefingForm: () => {
    abortScheduledBriefingPreviewRequest();
    revokeScheduledBriefingLogoPreviewUrl(
      get().scheduledBriefingLogo.previewUrl,
    );
    set(
      {
        scheduledBriefingDetails: scheduledBriefingDetailsInitialState,
        scheduledBriefingSchedule: scheduledBriefingScheduleInitialState,
        scheduledBriefingRecipients: scheduledBriefingRecipientsInitialState,
        scheduledBriefingLogo: scheduledBriefingLogoInitialState,
        editingScheduledBriefingId: null,
        isGeneratingScheduledBriefingPreview: false,
      },
      false,
      "[User] Scheduled Briefing: Reset form",
    );
  },

  setScheduledBriefingTitle: (title: string) =>
    set(
      (state) => ({
        scheduledBriefingDetails: {
          ...state.scheduledBriefingDetails,
          title,
        },
      }),
      false,
      "[User] Scheduled Briefing: Set title",
    ),

  setScheduledBriefingDescription: (description: string) =>
    set(
      (state) => ({
        scheduledBriefingDetails: {
          ...state.scheduledBriefingDetails,
          description,
        },
      }),
      false,
      "[User] Scheduled Briefing: Set description",
    ),

  setShowScheduledBriefingDescription: (showDescription: boolean) =>
    set(
      (state) => ({
        scheduledBriefingDetails: {
          ...state.scheduledBriefingDetails,
          showDescription,
          description: showDescription
            ? state.scheduledBriefingDetails.description
            : "",
        },
      }),
      false,
      "[User] Scheduled Briefing: Set show description",
    ),

  setScheduledBriefingPrompt: (prompt: string) =>
    set(
      (state) => ({
        scheduledBriefingDetails: {
          ...state.scheduledBriefingDetails,
          prompt,
        },
      }),
      false,
      "[User] Scheduled Briefing: Set prompt",
    ),

  setScheduledBriefingHasUserPrompt: (hasUserPrompt: boolean) =>
    set(
      (state) => ({
        scheduledBriefingDetails: {
          ...state.scheduledBriefingDetails,
          hasUserPrompt,
        },
      }),
      false,
      "[User] Scheduled Briefing: Set has user prompt",
    ),

  setScheduledBriefingScheduleTime: (scheduleTime: string) =>
    set(
      (state) => ({
        scheduledBriefingSchedule: {
          ...state.scheduledBriefingSchedule,
          scheduleTime,
        },
      }),
      false,
      "[User] Scheduled Briefing: Set schedule time",
    ),

  setScheduledBriefingTimezone: (timezone: TimezoneChoices | "") =>
    set(
      (state) => ({
        scheduledBriefingSchedule: {
          ...state.scheduledBriefingSchedule,
          timezone,
        },
      }),
      false,
      "[User] Scheduled Briefing: Set timezone",
    ),

  setScheduledBriefingRecipientInputValue: (inputValue: string) =>
    set(
      (state) => ({
        scheduledBriefingRecipients: {
          ...state.scheduledBriefingRecipients,
          inputValue,
        },
      }),
      false,
      "[User] Scheduled Briefing: Set recipient input",
    ),

  addScheduledBriefingRecipient: (raw: string) => {
    const email = raw.trim();
    if (!email) return;

    const { validEmails, invalidEmails } = get().scheduledBriefingRecipients;
    const existingValues = [
      ...validEmails,
      ...invalidEmails.map((entry) => entry.value),
    ];
    if (existingValues.includes(email)) return;

    if (isValidEmail(email)) {
      set(
        (state) => ({
          scheduledBriefingRecipients: {
            ...state.scheduledBriefingRecipients,
            validEmails: state.scheduledBriefingRecipients.validEmails.includes(
              email,
            )
              ? state.scheduledBriefingRecipients.validEmails
              : [...state.scheduledBriefingRecipients.validEmails, email],
          },
        }),
        false,
        "[User] Scheduled Briefing: Add valid recipient",
      );
      return;
    }

    set(
      (state) => ({
        scheduledBriefingRecipients: {
          ...state.scheduledBriefingRecipients,
          invalidEmails: state.scheduledBriefingRecipients.invalidEmails.some(
            (entry) => entry.value === email,
          )
            ? state.scheduledBriefingRecipients.invalidEmails
            : [
                ...state.scheduledBriefingRecipients.invalidEmails,
                toInvalidRecipientEntry(email),
              ],
        },
      }),
      false,
      "[User] Scheduled Briefing: Add invalid recipient",
    );
  },

  removeScheduledBriefingValidRecipient: (email: string) =>
    set(
      (state) => ({
        scheduledBriefingRecipients: {
          ...state.scheduledBriefingRecipients,
          validEmails: state.scheduledBriefingRecipients.validEmails.filter(
            (item) => item !== email,
          ),
        },
      }),
      false,
      "[User] Scheduled Briefing: Remove valid recipient",
    ),

  removeScheduledBriefingInvalidRecipient: (id: string) =>
    set(
      (state) => ({
        scheduledBriefingRecipients: {
          ...state.scheduledBriefingRecipients,
          invalidEmails: state.scheduledBriefingRecipients.invalidEmails.filter(
            (entry) => entry.id !== id,
          ),
        },
      }),
      false,
      "[User] Scheduled Briefing: Remove invalid recipient",
    ),

  setScheduledBriefingInvalidRecipientValue: (id: string, value: string) =>
    set(
      (state) => ({
        scheduledBriefingRecipients: {
          ...state.scheduledBriefingRecipients,
          invalidEmails: state.scheduledBriefingRecipients.invalidEmails.map(
            (entry) => (entry.id === id ? { ...entry, value } : entry),
          ),
        },
      }),
      false,
      "[User] Scheduled Briefing: Set invalid recipient value",
    ),

  commitScheduledBriefingInvalidRecipient: (id: string) => {
    const { invalidEmails } = get().scheduledBriefingRecipients;
    const entry = invalidEmails.find((item) => item.id === id);
    const email = entry?.value.trim() ?? "";
    if (!email) return;

    if (isValidEmail(email)) {
      set(
        (state) => ({
          scheduledBriefingRecipients: {
            ...state.scheduledBriefingRecipients,
            invalidEmails:
              state.scheduledBriefingRecipients.invalidEmails.filter(
                (item) => item.id !== id,
              ),
            validEmails: state.scheduledBriefingRecipients.validEmails.includes(
              email,
            )
              ? state.scheduledBriefingRecipients.validEmails
              : [...state.scheduledBriefingRecipients.validEmails, email],
          },
        }),
        false,
        "[User] Scheduled Briefing: Commit valid invalid recipient",
      );
      return;
    }

    set(
      (state) => ({
        scheduledBriefingRecipients: {
          ...state.scheduledBriefingRecipients,
          invalidEmails: state.scheduledBriefingRecipients.invalidEmails.map(
            (item) => (item.id === id ? { ...item, value: email } : item),
          ),
        },
      }),
      false,
      "[User] Scheduled Briefing: Commit invalid recipient",
    );
  },

  uploadScheduledBriefingLogo: (file: File) => {
    const { previewUrl: priorPreviewUrl } = get().scheduledBriefingLogo;
    revokeScheduledBriefingLogoPreviewUrl(priorPreviewUrl);

    set(
      {
        scheduledBriefingLogo: {
          logoFile: file,
          previewUrl: URL.createObjectURL(file),
          fileName: file.name,
          isRemoved: false,
        },
      },
      false,
      "[User] Scheduled Briefing: Select logo",
    );
  },

  removeScheduledBriefingLogo: () => {
    const { editingScheduledBriefingId, scheduledBriefingLogo } = get();
    const { logoFile, previewUrl } = scheduledBriefingLogo;
    const isEdit = editingScheduledBriefingId !== null;
    const hadLogoToClear = logoFile !== null || previewUrl !== null;

    revokeScheduledBriefingLogoPreviewUrl(previewUrl);

    set(
      {
        scheduledBriefingLogo: {
          ...scheduledBriefingLogoInitialState,
          isRemoved: isEdit && hadLogoToClear,
        },
      },
      false,
      "[User] Scheduled Briefing: Remove logo",
    );
  },

  createScheduledBriefing: async () => {
    set(
      { isCreatingScheduledBriefing: true },
      false,
      "[User] Scheduled Briefing: Start create",
    );

    try {
      const {
        resetScheduledBriefingForm,
        setCreateScheduledBriefingModalOpen,
      } = get();

      const result = await client.mutation(CreateScheduledBriefing, {
        input: buildScheduledBriefingInput(get()),
      });

      if (result.error) {
        toast.error("We couldn't create the scheduled briefing right now.");
        return false;
      }

      const payload = result.data?.createScheduledBriefing;
      if (payload?.__typename === "OperationInfo") {
        const errorMsg = payload.messages?.[0]?.message;
        toast.error(
          errorMsg ??
            "An error occurred while creating the scheduled briefing.",
        );
        return false;
      }

      if (payload?.__typename !== "ScheduledBriefingType") {
        toast.error("An error occurred while creating the scheduled briefing.");
        return false;
      }

      resetScheduledBriefingForm();
      setCreateScheduledBriefingModalOpen(false);
      toast({
        type: "success",
        title: "Scheduled briefing is successful",
        description: `"${payload.name}" was created.`,
      });

      return true;
    } catch (error) {
      console.error("Error creating scheduled briefing:", error);
      toast.error("An error occurred while creating the scheduled briefing.");
      return false;
    } finally {
      set(
        { isCreatingScheduledBriefing: false },
        false,
        "[User] Scheduled Briefing: Finish create",
      );
    }
  },

  updateScheduledBriefing: async () => {
    const scheduledBriefingId = get().editingScheduledBriefingId;
    if (!scheduledBriefingId) {
      return false;
    }

    set(
      { isUpdatingScheduledBriefing: true },
      false,
      "[User] Scheduled Briefing: Start update",
    );

    try {
      const {
        resetScheduledBriefingForm,
        setCreateScheduledBriefingModalOpen,
      } = get();

      const result = await client.mutation(UpdateScheduledBriefing, {
        input: buildScheduledBriefingUpdateInput(get(), scheduledBriefingId),
      });

      if (result.error) {
        toast.error("We couldn't update the scheduled briefing right now.");
        return false;
      }

      const payload = result.data?.updateScheduledBriefing;
      if (payload?.__typename === "OperationInfo") {
        const errorMsg = payload.messages?.[0]?.message;
        toast.error(
          errorMsg ??
            "An error occurred while updating the scheduled briefing.",
        );
        return false;
      }

      if (payload?.__typename !== "ScheduledBriefingType") {
        toast.error("An error occurred while updating the scheduled briefing.");
        return false;
      }

      resetScheduledBriefingForm();
      setCreateScheduledBriefingModalOpen(false);
      toast({
        type: "success",
        title: "Scheduled briefing updated",
        description: `"${payload.name}" was updated.`,
      });

      return true;
    } catch (error) {
      console.error("Error updating scheduled briefing:", error);
      toast.error("An error occurred while updating the scheduled briefing.");
      return false;
    } finally {
      set(
        { isUpdatingScheduledBriefing: false },
        false,
        "[User] Scheduled Briefing: Finish update",
      );
    }
  },

  deleteScheduledBriefing: async () => {
    const {
      deletingScheduledBriefingId: id,
      deletingScheduledBriefingName: briefingName,
    } = get();
    if (!id) {
      throw new Error(
        "deleteScheduledBriefing called without deletingScheduledBriefingId set; openDeleteScheduledBriefingModal(id, name) must run first.",
      );
    }

    set(
      { isDeletingScheduledBriefing: true },
      false,
      "[User] Scheduled Briefing: Start delete",
    );

    try {
      const result = await client.mutation(DeleteScheduledBriefing, {
        scheduledBriefingId: id,
      });

      if (result.error) {
        toast.error("We couldn't delete the scheduled briefing right now.");
        return false;
      }

      const payload = result.data?.deleteScheduledBriefing;
      if (payload?.__typename === "OperationInfo") {
        const errorMsg = payload.messages?.[0]?.message;
        toast.error(
          errorMsg ??
            "An error occurred while deleting the scheduled briefing.",
        );
        return false;
      }

      if (payload?.__typename !== "SuccessResponse") {
        toast.error("An error occurred while deleting the scheduled briefing.");
        return false;
      }

      get().closeDeleteScheduledBriefingModal();

      toast({
        type: "neutral",
        title: "Scheduled briefing deleted",
        description: `"${briefingName}" was deleted.`,
      });

      return true;
    } catch (error) {
      console.error("Error deleting scheduled briefing:", error);
      toast.error("An error occurred while deleting the scheduled briefing.");
      return false;
    } finally {
      set(
        { isDeletingScheduledBriefing: false },
        false,
        "[User] Scheduled Briefing: Finish delete",
      );
    }
  },

  generateScheduledBriefingPreview: async () => {
    abortScheduledBriefingPreviewRequest();
    const controller = new AbortController();
    scheduledBriefingPreviewController = controller;

    set(
      { isGeneratingScheduledBriefingPreview: true },
      false,
      "[User] Scheduled Briefing: Start preview",
    );

    try {
      const result = await client.mutation(
        GenerateScheduledBriefingPdfPreview,
        {
          input: buildGenerateBriefingPdfPreviewInput(get()),
        },
        { fetchOptions: { signal: controller.signal } },
      );

      if (controller.signal.aborted) return;

      if (result.error) {
        toast.error("We couldn't generate the preview right now.");
        return;
      }

      const payload = result.data?.generateBriefingPdfPreview;
      if (payload?.__typename === "OperationInfo") {
        const errorMsg = payload.messages?.[0]?.message;
        toast.error(
          errorMsg ??
            "An error occurred while generating the scheduled briefing preview.",
        );
        return;
      }

      if (payload?.__typename !== "BriefingPDFPreviewResponse") {
        toast.error(
          "An error occurred while generating the scheduled briefing preview.",
        );
        return;
      }

      const previewUrl = payload.url;
      if (!previewUrl) {
        toast.error(
          "An error occurred while generating the scheduled briefing preview.",
        );
        return;
      }

      if (!previewPdfInNewTab(previewUrl)) {
        toast.error("The preview link is invalid.");
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Error generating scheduled briefing preview:", error);
      toast.error(
        "An error occurred while generating the scheduled briefing preview.",
      );
    } finally {
      if (scheduledBriefingPreviewController === controller) {
        scheduledBriefingPreviewController = null;
      }
      if (!controller.signal.aborted) {
        set(
          { isGeneratingScheduledBriefingPreview: false },
          false,
          "[User] Scheduled Briefing: Finish preview",
        );
      }
    }
  },

  fetchScheduledBriefingById: async (id: string) => {
    if (!id) {
      return false;
    }

    set(
      { isFetchingScheduledBriefingById: true },
      false,
      "[User] Scheduled Briefing: Start fetch by id",
    );

    try {
      const result = await client.query(GetScheduledBriefing, {
        filters: { id },
      });

      if (result.error) {
        toast.error("We couldn't fetch the scheduled briefing right now.");
        set(
          { editingScheduledBriefingId: null },
          false,
          "[User] Scheduled Briefing: Fetch by id failed",
        );
        return false;
      }

      const briefing = result.data?.scheduledBriefings?.[0];
      if (!briefing) {
        toast.error("We couldn't find the scheduled briefing.");
        set(
          { editingScheduledBriefingId: null },
          false,
          "[User] Scheduled Briefing: Fetch by id not found",
        );
        return false;
      }

      set(
        hydrateScheduledBriefingFormFromApi(briefing),
        false,
        "[User] Scheduled Briefing: Fetch by id succeeded",
      );
      return true;
    } catch (error) {
      console.error("Error fetching scheduled briefing:", error);
      toast.error("An error occurred while fetching the scheduled briefing.");
      set(
        { editingScheduledBriefingId: null },
        false,
        "[User] Scheduled Briefing: Fetch by id error",
      );
      return false;
    } finally {
      set(
        { isFetchingScheduledBriefingById: false },
        false,
        "[User] Scheduled Briefing: Finish fetch by id",
      );
    }
  },

  openScheduledBriefingForEdit: async (id: string) => {
    const {
      resetScheduledBriefingForm,
      fetchScheduledBriefingById,
      setCreateScheduledBriefingModalOpen,
    } = get();

    resetScheduledBriefingForm();

    set(
      { editingScheduledBriefingId: id },
      false,
      "[User] Scheduled Briefing: Open for edit",
    );
    setCreateScheduledBriefingModalOpen(true);

    const success = await fetchScheduledBriefingById(id);
    if (!success) {
      setCreateScheduledBriefingModalOpen(false);
      return false;
    }

    return true;
  },
});
